// OVERRIDE FEATURE: Assets orchestration layer
// Dynamically loads banner configs and store-specific overrides

import StoreMap, { StoreOverride } from '../types/StoreMap';
// import A060 from './storeOverrides/A060.json';
import SPECIAL_THEME_STORE from './storeOverrides/SPECIAL_THEME_STORE.json';

// Type augmentation for Webpack's require.context
declare global {
  interface NodeRequire {
    context(directory: string, useSubdirectories: boolean, regExp: RegExp): {
      keys: () => string[];
      <T = any>(id: string): T;
    };
  }
}

// OVERRIDE FEATURE: Lazy-loaded cache for banner modules
let bannerModulesCache: Record<string, { default: StoreMap }> | null = null;
let bannersMapCache: Map<string, StoreMap> | null = null;

// OVERRIDE FEATURE: Lazy-loaded cache for override modules
let overrideModulesCache: Record<string, { default: StoreOverride }> | null = null;

// OVERRIDE FEATURE: Static override map
// in case of permanent overrides, 
// we optimize by avoiding the lookup
const staticOverrides: Record<string, StoreOverride> = {
  // 'A060': A060 as StoreOverride, // TODO: Enable when A060 store is ready for production
  'SPECIAL_THEME_STORE': SPECIAL_THEME_STORE as StoreOverride,
};

// OVERRIDE FEATURE: Initialize banner modules on first use
function getBannerModules(): Record<string, { default: StoreMap }> {
  if (bannerModulesCache === null) {
    const bannerContext = (require as any).context('./banners', false, /\.json$/);
    bannerModulesCache = {};
    for (const key of bannerContext.keys()) {
      const module = bannerContext(key);
      bannerModulesCache[key] = module;
    }
  }
  return bannerModulesCache;
}

// OVERRIDE FEATURE: Initialize override modules on first use
function getOverrideModules(): Record<string, { default: StoreOverride }> {
  if (overrideModulesCache === null) {
    const overrideContext = (require as any).context('./storeOverrides', false, /\.json$/);
    overrideModulesCache = {};
    for (const key of overrideContext.keys()) {
      const module = overrideContext(key);
      overrideModulesCache[key] = { default: module.default || module } as { default: StoreOverride };
    }
  }
  return overrideModulesCache;
}

// OVERRIDE FEATURE: Initialize banners map on first use
function getBannersMap(): Map<string, StoreMap> {
  if (bannersMapCache === null) {
    bannersMapCache = new Map<string, StoreMap>();
    const bannerModules = getBannerModules();
    for (const moduleEntry of Object.values(bannerModules)) {
      const module = moduleEntry.default || moduleEntry;
      if (module) {
        bannersMapCache.set(module.banner_code, module);
      }
    }
  }
  return bannersMapCache;
}

/**
 * OVERRIDE FEATURE: Get banner configuration by banner_code
 * @param bannerCode - The banner code to load
 * @returns Banner configuration or undefined if not found
 */
export function getBannerConfig(bannerCode: string): StoreMap | undefined {
  return getBannersMap().get(bannerCode);
}

/**
 * OVERRIDE FEATURE: Get store-specific override by storeId
 * @param storeId - The store ID to load overrides for
 * @returns Store override configuration or undefined if not found
 */
export function getStoreOverride(storeId: string): StoreOverride | undefined {
  try {
    // Strip a leading region prefix (e.g. "5-Q339" -> "Q339") so both the
    // wire-format GSID and the bare store number resolve to the same asset.
    const normalized = storeId.replace(/^\d+-/, '');

    // Try static overrides first
    if (staticOverrides[normalized]) {
      return staticOverrides[normalized];
    }

    // Fall back to require.context
    const overrideModules = getOverrideModules();
    const module = overrideModules[`./${normalized}.json`];

    if (module?.default) {
      // Validate: filename storeId matches content storeId
      if (module.default.storeId !== normalized) {
        console.warn(
          `Store override mismatch: file "${normalized}.json" contains storeId "${module.default.storeId}". ` +
          `This may indicate a misconfigured or misnamed override file.`
        );
      }
      return module.default;
    }

    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * OVERRIDE FEATURE: Get all banners
 * @returns Array of all banner configurations
 */
export function getAllBanners(): StoreMap[] {
  return Array.from(getBannersMap().values());
}

const assetOrchestration = {
  getBannerConfig,
  getStoreOverride,
  getAllBanners,
};

export default assetOrchestration;
